import { skipToken, useQuery } from "@apollo/client/react";
import type { VariantProps } from "class-variance-authority";
import Link from "next/link";
import type { AnchorHTMLAttributes, DetailedHTMLProps, ReactNode } from "react";
import { use } from "react";
import { useMemo } from "react";
import { LuMail } from "react-icons/lu";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import type { buttonVariants } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { UserContext } from "@/contexts/user-context";
import { PaidMessagingSettingsByPublicKeyDocument } from "@/graphql/codegen/graphql";
import { formatUSDCents } from "@/utils/currency";
import { cn } from "@/utils/lib";
import { buildDMThreadIdentifier } from "@/utils/messages";

interface MessageDeepLinkProps {
  publicKey: string;
  isTooltip?: boolean;
  content?: ReactNode;
  buttonVariant?: VariantProps<typeof buttonVariants>["variant"];
  buttonClassName?: string;
}
export const MessageLink = ({
  publicKey,
  className,
  isTooltip = true,
  content,
  buttonVariant = "outline",
  buttonClassName,
  ...rest
}: MessageDeepLinkProps &
  Omit<
    DetailedHTMLProps<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      HTMLAnchorElement
    >,
    "href"
  >) => {
  const { currentUserPublicKey } = use(UserContext);
  const href =
    publicKey && currentUserPublicKey && publicKey !== currentUserPublicKey
      ? `/messages?threadId=${buildDMThreadIdentifier(publicKey, currentUserPublicKey)}`
      : "/messages";
  const { data } = useQuery(
    PaidMessagingSettingsByPublicKeyDocument,
    publicKey && currentUserPublicKey && publicKey !== currentUserPublicKey
      ? {
          variables: {
            publicKey,
            viewerPublicKey: currentUserPublicKey,
            associationValueWildcard: `%${currentUserPublicKey}%default-key-false`,
          },
        }
      : skipToken,
  );
  let minTipUsdCents = 0;

  const globalMessagingSettings =
    data?.accountByPublicKey?.paidMessagingSettings?.nodes?.[0];
  const customUserMessagingSettings =
    data?.accountByPublicKey?.customMessagingUserSettings?.nodes?.[0];
  const customThreadMessagingSettings =
    data?.accountByPublicKey?.customMessagingThreadSettings?.nodes?.[0];
  const viewerIsFollowing =
    (data?.accountByPublicKey?.viewerIsFollowing?.totalCount ?? 0) > 0;

  if (globalMessagingSettings?.extraData?.FeePerMessageUsdCents) {
    if (viewerIsFollowing) {
      minTipUsdCents = Number(
        globalMessagingSettings.extraData.FollowingFeePerMessageUsdCents ?? 0,
      );
    } else {
      minTipUsdCents = Number(
        globalMessagingSettings.extraData.FeePerMessageUsdCents,
      );
    }
  }

  if (customUserMessagingSettings?.extraData?.FeePerMessageUsdCents) {
    minTipUsdCents = Number(
      customUserMessagingSettings.extraData.FeePerMessageUsdCents,
    );
  }

  if (customThreadMessagingSettings?.extraData?.FeePerMessageUsdCents) {
    minTipUsdCents = Number(
      customThreadMessagingSettings.extraData.FeePerMessageUsdCents,
    );
  }

  const tooltipText =
    minTipUsdCents > 0
      ? `Send DM (min. ${formatUSDCents(minTipUsdCents)})`
      : "Send DM";

  const linkContent = useMemo(() => {
    return (
      <Link
        {...rest}
        href={href}
        className={cn(
          `
            text-lg text-foreground underline-offset-4
            hover:text-muted-foreground hover:underline
          `,
          className,
        )}
        aria-label="Send a message"
        prefetch={false}
      >
        <Button
          variant={buttonVariant}
          className={buttonClassName}
          size="icon"
          as="div"
        >
          {content || <LuMail />}
        </Button>
      </Link>
    );
  }, [rest, href, className, buttonVariant, content, buttonClassName]);

  if (!isTooltip) {
    return linkContent;
  }

  return <InfoTooltip text={tooltipText}>{linkContent}</InfoTooltip>;
};
